import { _isNotBlank, _notNil } from '@/littledash';
import type { SendInputFailure } from '@/model/Agent.model';
import type { FC } from 'react';

interface SendInputFailuresProps {
  failures: Array<SendInputFailure>;
  testId?: string;
}

const locationOf = (failure: SendInputFailure): string => {
  const parts: Array<string> = [];
  if (_notNil(failure.row_number)) {
    parts.push(`Row ${failure.row_number}`);
  }
  if (_notNil(failure.line_number)) {
    parts.push(`line ${failure.line_number}`);
  }
  if (_notNil(failure.column_name)) {
    parts.push(`column '${failure.column_name}'`);
  } else if (_notNil(failure.column_position)) {
    parts.push(`column ${failure.column_position}`);
  }
  return parts.join(', ');
};

/**
 * Lists why a SEND run stopped on unreadable files. Deliberately plain, and states that nothing was
 * checked, so a stopped run never reads as one that found nothing wrong.
 */
const SendInputFailures: FC<SendInputFailuresProps> = ({ failures, testId = 'send-input-failures' }) => (
  <div data-test-component="SendInputFailures" data-testid={testId}>
    <p className="dark-gray f6 mb3" data-test-element="summary">
      The run stopped because the upload could not be read. Nothing was checked against SEND, so these are not SEND
      findings: fix the problems below and upload the files again.
    </p>
    <ul className="list pl0 ma0" data-test-element="failures">
      {failures.map((failure) => {
        const location = locationOf(failure);
        return (
          <li
            key={`${failure.attachment_api_id ?? 'upload'}-${failure.kind}`}
            className="pv2 bb b--moon-gray f6"
            data-test-element="failure"
            data-test-key={failure.attachment_api_id ?? 'upload'}
          >
            <div className="fw5 dark-gray" data-test-element="failure-file">
              {failure.filename ?? 'The upload as a whole'}
            </div>
            {_isNotBlank(location) && (
              <div className="mid-gray" data-test-element="failure-location">
                {location}
              </div>
            )}
            <div className="dark-gray" data-test-element="failure-message">
              {failure.message}
            </div>
            {_notNil(failure.configured_limit) && (
              <div className="mid-gray" data-test-element="failure-limit">
                Configured limit: {failure.configured_limit.toLocaleString('en-US')}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

export default SendInputFailures;
